/**
 * DecomposeCommandPalette — Step 10
 * ─────────────────────────────────
 * Cmd+K / Ctrl+K command palette for the Define Decomposition workbench.
 *
 * Props:
 *   isOpen        {bool}     Whether the palette is currently visible
 *   onClose       {fn}       Called when the palette should close
 *   commands      {array}    Command objects — see shape below
 *   assetNames    {string[]} COBOL program names for fuzzy asset search
 *   onFindProgram {fn}       Called with a program name when user picks "Find program: X"
 *
 * Command shape:
 *   {
 *     id:       string        — unique stable key
 *     label:    string        — display text
 *     group:    string        — section label (Navigation / Domains / Layout / Actions)
 *     icon?:    ReactNode     — optional Lucide icon element
 *     action:   () => void    — called when the command is executed
 *     disabled? bool          — greyed-out, cannot execute
 *   }
 *
 * No external dependencies — self-contained modal with keyboard navigation.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import './DecomposeCommandPalette.css'

// Maximum number of asset ("Find program") results shown below commands
const MAX_ASSET_RESULTS = 6


// ─────────────────────────────────────────────────────────────────────────────

export default function DecomposeCommandPalette({
  isOpen,
  onClose,
  commands = [],
  assetNames = [],
  onFindProgram,
}) {
  const [query,       setQuery]       = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef(null)
  const listRef  = useRef(null)

  // ── Reset state when palette opens ─────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setActiveIndex(0)
    }
  }, [isOpen])

  // ── Focus input when palette opens ─────────────────────────────────────────
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay ensures the element is mounted and visible before focusing
      const t = setTimeout(() => inputRef.current?.focus(), 0)
      return () => clearTimeout(t)
    }
  }, [isOpen])

  // ── Build filtered command list ─────────────────────────────────────────────
  const lq = query.trim().toLowerCase()

  const filteredCommands = commands.filter(
    c => !c.hidden && (!lq || c.label.toLowerCase().includes(lq))
  )

  // ── Asset search results (dynamic) ─────────────────────────────────────────
  const assetResults = lq
    ? assetNames
        .filter(n => n.toLowerCase().includes(lq))
        .slice(0, MAX_ASSET_RESULTS)
        .map(n => ({
          id:     `asset:${n}`,
          label:  `Find program: ${n}`,
          group:  'Programs',
          action: () => onFindProgram?.(n),
        }))
    : []

  // ── Flat list of all visible items (for keyboard index tracking) ────────────
  const allItems = [...filteredCommands, ...assetResults]

  // ── Reset active index when query changes ───────────────────────────────────
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // ── Scroll active item into view ────────────────────────────────────────────
  useEffect(() => {
    if (!listRef.current) return
    const el = listRef.current.querySelector('[data-active="true"]')
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  // ── Execute a command and close ─────────────────────────────────────────────
  const execute = useCallback((item) => {
    if (!item || item.disabled) return
    item.action?.()
    onClose()
  }, [onClose])

  // ── Keyboard navigation ─────────────────────────────────────────────────────
  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'Escape':
        onClose()
        break
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex(i => Math.min(i + 1, allItems.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex(i => Math.max(i - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        execute(allItems[activeIndex])
        break
      default:
        break
    }
  }, [onClose, allItems, activeIndex, execute])

  // ── Render ──────────────────────────────────────────────────────────────────
  if (!isOpen) return null

  // Build items with separators between groups
  const renderItems = () => {
    if (allItems.length === 0) {
      return (
        <div className="dcp-empty">
          No commands match <em>"{query}"</em>
        </div>
      )
    }

    const rendered = []
    let flatIdx   = 0
    let lastGroup = null

    // ── Command items (with group separators) ─────────────────────────────
    for (const item of filteredCommands) {
      if (item.group !== lastGroup) {
        if (lastGroup !== null) {
          rendered.push(<div key={`sep-${item.group}-${flatIdx}`} className="dcp-separator" />)
        }
        rendered.push(
          <div key={`grp-${item.group}`} className="dcp-group-hdr">
            {item.group}
          </div>
        )
        lastGroup = item.group
      }

      const idx       = flatIdx++
      const isActive  = idx === activeIndex
      rendered.push(
        <button
          key={item.id}
          data-active={isActive}
          className={[
            'dcp-item',
            isActive      ? 'dcp-item-active'   : '',
            item.disabled ? 'dcp-item-disabled'  : '',
          ].filter(Boolean).join(' ')}
          onMouseEnter={() => setActiveIndex(idx)}
          onClick={() => execute(item)}
          disabled={!!item.disabled}
          role="option"
          aria-selected={isActive}
          tabIndex={-1}
        >
          {item.icon && <span className="dcp-item-icon">{item.icon}</span>}
          <span className="dcp-item-label">{item.label}</span>
          <span className="dcp-item-group-tag">{item.group}</span>
        </button>
      )
    }

    // ── Asset search results ──────────────────────────────────────────────
    if (assetResults.length > 0) {
      if (filteredCommands.length > 0) {
        rendered.push(<div key="sep-assets" className="dcp-separator" />)
      }
      rendered.push(
        <div key="grp-programs" className="dcp-group-hdr">Programs</div>
      )
      for (const item of assetResults) {
        const idx      = flatIdx++
        const isActive = idx === activeIndex
        rendered.push(
          <button
            key={item.id}
            data-active={isActive}
            className={['dcp-item', isActive ? 'dcp-item-active' : ''].filter(Boolean).join(' ')}
            onMouseEnter={() => setActiveIndex(idx)}
            onClick={() => execute(item)}
            role="option"
            aria-selected={isActive}
            tabIndex={-1}
          >
            <span className="dcp-item-label">{item.label}</span>
            <span className="dcp-item-group-tag">Programs</span>
          </button>
        )
      }
    }

    return rendered
  }

  return (
    /* Backdrop — click outside to close */
    <div
      className="dcp-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      {/* Panel — stop propagation so clicks inside don't close */}
      <div
        className="dcp-panel"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search row */}
        <div className="dcp-search-row">
          <Search size={14} className="dcp-search-ico" aria-hidden />
          <input
            ref={inputRef}
            className="dcp-input"
            type="text"
            placeholder="Search commands…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            aria-label="Search commands"
            aria-autocomplete="list"
          />
          <button
            className="dcp-close-btn"
            onClick={onClose}
            tabIndex={-1}
            aria-label="Close command palette"
          >
            <X size={12} />
          </button>
        </div>

        {/* Command list */}
        <div ref={listRef} className="dcp-list" role="listbox" aria-label="Commands">
          {renderItems()}
        </div>

        {/* Footer keyboard hints */}
        <div className="dcp-footer">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>↵</kbd> execute</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  )
}
